import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs, UltimateSenderType } from "../../../../../../types/walletTypes";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { KlashaCNYQuotation } from "../../../../redux/slice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { formatCurrencyAmount } from "../../../../../../utils/currency";
import { debounce } from "lodash";
import { Link } from "react-router-dom";
import {
  ContentWrapper,
  InputWrapper,
  GenericTitle,
  GenericSubTitle,
  ItemWrapper,
  RWrapper,
  Error,
  RateBox,
  BalanceExceptionWrapper,
} from "../../../shared/styles";
import CustomRadio from "../../../../../../components/common/RadioInput";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import InputAndSelect from "../../../../../../components/common/InputAndSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { ReactComponent as NotifyIcon } from "../../../../../../assets/icons/notify-icon.svg";
import {
  CHINA_PAYMENT_METHODS_B2B,
  CHINA_PAYMENT_METHODS_C2C,
  BUSINESS_TYPE_OPTIONS,
  INDIVIDUAL_TYPE_OPTIONS,
  C2C_PURPOSE_OPTIONS,
} from "../../../shared/const";
import { getChinaTransactionLimit } from "../../../shared/helpers";
import { LIST_OF_CHINA_CNY_PAYOUT_CURRENCIES, MIN_CNY_AMOUNT } from "../../../../const";
import { usePurposeCodeLookup } from "../../../shared/hooks/usePurposeCodeLookup";

type WalletProps = {
  currency: string;
  availableBalance: number;
};

interface ChinaAmountFormProps {
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const ChinaAmountForm: React.FC<ChinaAmountFormProps> = ({ handleNext }) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData = useAppSelector((state) => (state.formData as any)?.asiaPayoutData?.cn || {});
  const { keys } = useAppSelector((state) => state.settings);
  const { wallets, loading } = useAppSelector((state) => state.wallets || {});
  const { businessId } = useAppSelector((state) => state.users);

  const { getPurposeCodeByDescription } = usePurposeCodeLookup();

  const [userData, setUserData] = useState<FormInputs>(() => ({
    sourceCurrency: asiaPayoutData?.sourceCurrency || "USD",
    ...asiaPayoutData,
  }));

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: userData,
    mode: "onChange"
  });

  useEffect(() => {
    if (asiaPayoutData && Object.keys(asiaPayoutData).length > 0) {
      setUserData((prev) => ({
        sourceCurrency: "USD",
        ...prev,
        ...asiaPayoutData,
      }));
      reset({ sourceCurrency: "USD", ...asiaPayoutData });
    }
  }, [asiaPayoutData, reset]);

  const sourceCurrency = userData?.sourceCurrency || "USD";
  const filteredWallets = (wallets || []) as WalletProps[];
  const CURRENT_WALLET = filteredWallets.find((w) => w.currency === sourceCurrency);

  const handleDataUpdate = (field: keyof FormInputs, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValue(field, value);
  };

  const handleAmountChange = (enteredAmount: any) => {
    const numAmount = typeof enteredAmount === "number" ? enteredAmount : parseFloat(enteredAmount) || 0;
    setUserData((prev) => ({
      ...prev,
      amount: numAmount,
    }));
    setValue("amount", numAmount);
  };

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!CURRENT_WALLET) return;
    const availableBalance = parseFloat(
      Number(CURRENT_WALLET.availableBalance).toFixed(2)
    );
    const totalAmount = parseFloat((userData?.totalAmount ?? 0).toFixed(2));
    if (availableBalance < totalAmount) {
      handleDataUpdate("balanceError", true);
    } else {
      handleDataUpdate("balanceError", false);
    }
  }, [CURRENT_WALLET, userData?.totalAmount]);

  const requestIdRef = useRef(0);

  const debouncedGetKlashaQuotation = useCallback(
    debounce(async (currentData, currentBusinessId, currentReqId) => {
      const activeSourceCurrency = currentData?.sourceCurrency || "USD";
      const { maxLimit: currentMax } = getChinaTransactionLimit(
        currentData?.paymentMethod,
        undefined,
        currentData?.purpose || currentData?.description
      );
      const parsedAmount = Number(currentData?.amount);
      if (
        !currentData?.amount ||
        parsedAmount < MIN_CNY_AMOUNT ||
        parsedAmount > currentMax ||
        !currentData?.paymentMethod ||
        !currentBusinessId
      ) {
        return;
      }

      let service = "BANK_ACCOUNT";
      let serviceCode: string | undefined;
      let transferType = "B2B";

      if (currentData.paymentMethod === "unionpay") {
        service = "BANK_CARD";
        serviceCode = "UNIONPAY";
        transferType = "C2C";
      } else if (currentData.paymentMethod === "alipay") {
        service = "WALLET";
        serviceCode = "ALIPAY";
        transferType = "C2C";
      } else {
        service = "BANK_ACCOUNT";
        transferType = "B2B";
      }

      const data: Record<string, any> = {
        destinationCurrency: "CNY",
        destinationAmount: currentData.amount,
        sourceCurrency: activeSourceCurrency,
        service,
        ...(serviceCode ? { serviceCode } : {}),
        transferType,
        fundSource: "CASH",
      };

      try {
        const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
          currentBusinessId,
          data
        );
        const payload = { message: encryptedMessage };
        const token = publicKey;

        const action = await dispatch(
          KlashaCNYQuotation({ message: payload, token })
        );

        if (currentReqId !== requestIdRef.current) return;

        if (KlashaCNYQuotation.fulfilled.match(action)) {
          const { fxRate, fee, sourceAmount, expiration, id, reference } = action.payload;
          const convertedValue = Number(currentData.amount / fxRate);

          setUserData((prev) => ({
            ...prev,
            sourceCurrency: activeSourceCurrency,
            rate: fxRate,
            convertedAmount: convertedValue,
            totalAmount: Number(sourceAmount + fee),
            fee,
            expiration,
            quotationId: id,
            reference,
            sourceAmount,
            rateError: false,
          }));
        } else {
          setUserData((prev) => ({
            ...prev,
            rate: null,
            fee: null,
            totalAmount: 0,
            rateError: true,
          }));
        }
      } catch (err) {
        if (currentReqId !== requestIdRef.current) return;
        setUserData((prev) => ({
          ...prev,
          rateError: true,
        }));
      }
    }, 800),
    [dispatch]
  );

  const amount = userData?.amount;
  const paymentMethod = userData?.paymentMethod;
  const purpose = userData?.purpose || userData?.description;

  const { maxLimit, routeName } = getChinaTransactionLimit(
    paymentMethod,
    undefined,
    purpose
  );

  const numAmount = typeof amount === "number" ? amount : parseFloat(amount) || 0;
  const isBelowMin = Boolean(amount && numAmount < MIN_CNY_AMOUNT);
  const isAboveMax = Boolean(amount && numAmount > maxLimit);

  useEffect(() => {
    if (
      amount &&
      numAmount >= MIN_CNY_AMOUNT &&
      numAmount <= maxLimit &&
      paymentMethod &&
      sourceCurrency &&
      businessId
    ) {
      const nextReqId = requestIdRef.current + 1;
      requestIdRef.current = nextReqId;
      debouncedGetKlashaQuotation(
        { ...userData, sourceCurrency, purpose },
        businessId,
        nextReqId
      );
    } else {
      debouncedGetKlashaQuotation.cancel();
      requestIdRef.current++;
      setUserData((prev) => ({
        ...prev,
        rate: null,
        fee: null,
        totalAmount: 0,
        convertedAmount: 0,
        quotationId: undefined,
        reference: undefined,
        sourceAmount: 0,
        rateError: false,
      }));
    }
  }, [
    amount,
    numAmount,
    maxLimit,
    paymentMethod,
    purpose,
    sourceCurrency,
    businessId,
    debouncedGetKlashaQuotation,
  ]);

  useEffect(() => {
    return () => {
      debouncedGetKlashaQuotation.cancel();
    };
  }, [debouncedGetKlashaQuotation]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const selectedDesc = data.description || userData?.description || data.purpose || userData?.purpose;
    const purposeCode = getPurposeCodeByDescription(selectedDesc);

    const updatedData: FormInputs = {
      ...data,
      ...userData,
      description: selectedDesc,
      purpose: selectedDesc,
      purposeCode,
    };
    dispatch(savedAsiaPayoutData({ country: "cn", data: updatedData }));
    handleNext?.();
  };

  const handleSenderIdentityChange = (identity: UltimateSenderType) => {
    if (identity === "Yes") {
      const nextSenderType = "Business";
      const nextReceiverType = "Business";
      const nextPaymentMethod = "bank_account";
      const nextPurpose = "Payment for services";
      const matchedCode = getPurposeCodeByDescription(nextPurpose);
      const classification = "Service";

      setUserData((prev) => ({
        ...prev,
        senderIdentity: identity,
        senderType: nextSenderType,
        receiverType: nextReceiverType,
        paymentMethod: nextPaymentMethod,
        purpose: nextPurpose,
        description: nextPurpose,
        purposeCode: matchedCode,
        paymentClassification: classification,
      }));

      setValue("senderIdentity", identity);
      setValue("senderType", nextSenderType);
      setValue("receiverType", nextReceiverType);
      setValue("paymentMethod", nextPaymentMethod);
      setValue("purpose", nextPurpose);
      setValue("description", nextPurpose);
      setValue("purposeCode", matchedCode);
      setValue("paymentClassification", classification);
    } else {
      const nextSenderType = "Individual";
      const nextReceiverType = "Individual";
      const nextPaymentMethod = "alipay";
      const nextPurpose = "Personal Remittance";
      const matchedCode = getPurposeCodeByDescription(nextPurpose);
      const classification = "Service";

      setUserData((prev) => ({
        ...prev,
        senderIdentity: identity,
        senderType: nextSenderType,
        receiverType: nextReceiverType,
        paymentMethod: nextPaymentMethod,
        purpose: nextPurpose,
        description: nextPurpose,
        purposeCode: matchedCode,
        paymentClassification: classification,
      }));

      setValue("senderIdentity", identity);
      setValue("senderType", nextSenderType);
      setValue("receiverType", nextReceiverType);
      setValue("paymentMethod", nextPaymentMethod);
      setValue("purpose", nextPurpose);
      setValue("description", nextPurpose);
      setValue("purposeCode", matchedCode);
      setValue("paymentClassification", classification);
    }
  };

  const senderTypeOptions =
    userData?.senderIdentity === "Yes" ? BUSINESS_TYPE_OPTIONS : INDIVIDUAL_TYPE_OPTIONS;

  const receiverTypeOptions =
    userData?.senderIdentity === "Yes" ? BUSINESS_TYPE_OPTIONS : INDIVIDUAL_TYPE_OPTIONS;

  const paymentMethodOptions =
    userData?.senderIdentity === "Yes" ? CHINA_PAYMENT_METHODS_B2B : CHINA_PAYMENT_METHODS_C2C;

  const disableButton =
    loading ||
    !isValid ||
    !userData?.amount ||
    isBelowMin ||
    isAboveMax ||
    !userData?.rate ||
    userData?.balanceError;

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <GenericTitle>Are you the ultimate sender?</GenericTitle>
        <GenericSubTitle>
          Accurate identification is crucial for compliance with financial regulations.
        </GenericSubTitle>
        <Controller
          name="senderIdentity"
          control={control}
          render={({ field }) => (
            <ItemWrapper style={{ marginTop: "10px" }}>
              {["Yes", "No"].map((option) => (
                <RWrapper key={option}>
                  <CustomRadio
                    label={option}
                    value={option}
                    onClick={() => {
                      field.onChange(option);
                      handleSenderIdentityChange(option as UltimateSenderType);
                    }}
                    selected={field.value === option}
                  />
                </RWrapper>
              ))}
            </ItemWrapper>
          )}
        />
        {errors?.senderIdentity?.message && (
          <Error>{String(errors.senderIdentity.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <CustomSelect
          $width="100%"
          placeholder="Beneficiary country"
          options={[{ label: "China", value: "CN" }]}
          value="CN"
          lightBorder
          disabled
        />
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="senderType"
          control={control}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={senderTypeOptions}
              onChange={(val) => {
                field.onChange(val);
                handleDataUpdate("senderType", val);
              }}
              $width="100%"
              placeholder="Sender type"
              lightBorder
              disabled={!userData?.senderIdentity}
            />
          )}
        />
        {errors?.senderType?.message && (
          <Error>{String(errors.senderType.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverType"
          control={control}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={receiverTypeOptions}
              onChange={(val) => {
                field.onChange(val);
                handleDataUpdate("receiverType", val);
              }}
              $width="100%"
              placeholder="Receiver type"
              lightBorder
              disabled={!userData?.senderIdentity}
            />
          )}
        />
        {errors?.receiverType?.message && (
          <Error>{String(errors.receiverType.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="paymentMethod"
          control={control}
          rules={{ required: "Payment method is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="Payment method"
              options={paymentMethodOptions}
              onChange={(val) => {
                field.onChange(val);
                handleDataUpdate("paymentMethod", val);
              }}
              value={field.value || userData?.paymentMethod || undefined}
              lightBorder
              disabled={!userData?.senderIdentity}
            />
          )}
        />
        {errors?.paymentMethod?.message && (
          <Error>{String(errors.paymentMethod.message)}</Error>
        )}
      </InputWrapper>

      {userData?.senderIdentity === "No" && (
        <InputWrapper>
          <Controller
            name="description"
            control={control}
            rules={{ required: "Purpose is required" }}
            render={({ field }) => (
              <CustomSelect
                $width="100%"
                placeholder="Purpose"
                options={C2C_PURPOSE_OPTIONS}
                onChange={(val) => {
                  field.onChange(val);
                  handleDataUpdate("description", val);
                  const matchedCode = getPurposeCodeByDescription(val);
                  handleDataUpdate("purposeCode", matchedCode);
                  const classification = val === "Payments for goods" ? "Goods" : "Service";
                  handleDataUpdate("paymentClassification", classification);
                }}
                value={field.value || userData?.description || userData?.purpose || undefined}
                lightBorder
                disabled={!userData?.senderIdentity}
              />
            )}
          />
          {errors?.description?.message && (
            <Error>{String(errors.description.message)}</Error>
          )}
        </InputWrapper>
      )}

      <InputWrapper>
        <Controller
          name="amount"
          control={control}
          rules={{ required: "Amount is required" }}
          render={({ field }) => (
            <InputAndSelect
              amountValue={field.value ?? userData?.amount}
              width="100%"
              placeholder="Amount"
              options={[{ label: "CNY", value: "CNY" }]}
              selectValue="CNY"
              onAmountChange={(val) => {
                field.onChange(val);
                handleAmountChange(val);
              }}
            />
          )}
        />
        {errors?.amount?.message && (
          <Error>{String(errors.amount.message)}</Error>
        )}
        {isBelowMin && !errors?.amount?.message && (
          <Error>Minimum payout amount is {MIN_CNY_AMOUNT.toLocaleString()} CNY</Error>
        )}
        {isAboveMax && !errors?.amount?.message && (
          <Error>
            Maximum transaction limit for {routeName} is {maxLimit.toLocaleString()} CNY
          </Error>
        )}
      </InputWrapper>

      {userData?.rate != null && (
        <RateBox>
          <span className="fee-box">
            x <span>{formatCurrencyAmount("", userData?.rate ?? 0, 4)}</span>
          </span>
          <span>Exchange rate</span>
        </RateBox>
      )}

      {userData?.fee != null && (
        <RateBox>
          <span className="fee-box">
            + <span>{formatCurrencyAmount("", userData?.fee ?? 0, 2)}</span>
          </span>
          <span>Processing fee</span>
        </RateBox>
      )}

      {userData?.rateError && (
        <Error>Exchange rate unavailable. Please try again or contact support.</Error>
      )}

      {userData?.totalAmount > 0 && (
        <div>
          <Controller
            name="sourceCurrency"
            control={control}
            render={({ field }) => (
              <InputAndSelect
                amountValue={userData?.totalAmount?.toFixed(2) ?? ""}
                width="100%"
                placeholder="Total you will be charged"
                options={LIST_OF_CHINA_CNY_PAYOUT_CURRENCIES}
                selectValue={field.value || sourceCurrency}
                onSelectChange={(curr) => {
                  field.onChange(curr);
                  handleDataUpdate("sourceCurrency", curr);
                }}
              />
            )}
          />
          {userData?.balanceError && (
            <BalanceExceptionWrapper>
              <NotifyIcon /> <span>Exceeds wallet balance.</span>
              <Link to={`/wallets/${sourceCurrency.toLowerCase()}`}> Fund wallet</Link>
            </BalanceExceptionWrapper>
          )}
        </div>
      )}

      <Button
        label="Continue"
        onClick={handleSubmit(onSubmit)}
        height="48px"
        disabled={disableButton}
      />
    </ContentWrapper>
  );
};

export default ChinaAmountForm;
